import axios from 'axios';
import activityMonitor from '../utils/activityMonitor';
import { shouldRefreshToken, getTokenExpiryTime } from '../utils/tokenUtils';
import JWT_CONFIG from '../config/jwtConfig';

class ApiService {
  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || "http://localhost:5002/api",
      timeout: 10000,
    });

    this.refreshThreshold = JWT_CONFIG.REFRESH_THRESHOLD;
    this.isProactiveRefreshing = false;
    this.proactiveRefreshPromise = null;

    this.setupInterceptors();
  }

  // setupInterceptors() {
  //   // Request interceptor
  //   this.api.interceptors.request.use(
  //     (config) => {
  //       // Add correlation ID for tracking
  //       config.headers['X-Correlation-ID'] = this.generateCorrelationId();

  //       // Add auth token if available
  //       const token = localStorage.getItem('auth-storage');
  //       if (token) {
  //         try {
  //           const authData = JSON.parse(token);
  //           if (authData?.state?.tokens?.accessToken) {
  //             config.headers['Authorization'] = `Bearer ${authData.state.tokens.accessToken}`;
  //           }
  //         } catch (e) {
  //           // Silent fail for invalid token format
  //         }
  //       }

  //       return config;
  //     },
  //     (error) => {
  //       return Promise.reject(error);
  //     }
  //   );

  //   // Response interceptor
  //   this.api.interceptors.response.use(
  //     (response) => {
  //       return response;
  //     },
  //     async (error) => {
  //       const originalRequest = error.config;

  //       // Handle 401 errors (token expired)
  //       if (error.response?.status === 401 && !originalRequest._retry) {
  //         originalRequest._retry = true;

  //         // Don't try to refresh if this is already a refresh token request
  //         if (originalRequest.url?.includes('/auth/refresh-token')) {
  //           localStorage.removeItem('auth-storage');
  //           localStorage.removeItem('accessToken');
  //           localStorage.removeItem('refreshToken');
  //           localStorage.removeItem('user');
  //           window.location.href = '/auth/login';
  //           return Promise.reject(error);
  //         }

  //         try {
  //           // Try to refresh token
  //           const authStore = await import('../store/authStore');
  //           const refreshSuccess = await authStore.default.getState().refreshToken();

  //           if (refreshSuccess) {
  //             // Update the authorization header with new token
  //             const token = localStorage.getItem('auth-storage');
  //             if (token) {
  //               const authData = JSON.parse(token);
  //               if (authData?.state?.tokens?.accessToken) {
  //                 originalRequest.headers['Authorization'] = `Bearer ${authData.state.tokens.accessToken}`;
  //               }
  //             }
  //             // Retry original request with new token
  //             return this.api(originalRequest);
  //           } else {
  //             // Refresh failed, clear storage and redirect to login
  //             localStorage.removeItem('auth-storage');
  //             localStorage.removeItem('accessToken');
  //             localStorage.removeItem('refreshToken');
  //             localStorage.removeItem('user');
  //             window.location.href = '/auth/login';
  //             return Promise.reject(error);
  //           }
  //         } catch (refreshError) {
  //           // Refresh failed, clear storage and redirect to login
  //           localStorage.removeItem('auth-storage');
  //           localStorage.removeItem('accessToken');
  //           localStorage.removeItem('refreshToken');
  //           localStorage.removeItem('user');
  //           window.location.href = '/auth/login';
  //           return Promise.reject(error);
  //         }
  //       }

  //       // Handle network errors
  //       if (!error.response) {
  //         error.message = 'Network error. Please check your connection.';
  //       }

  //       return Promise.reject(error);
  //     }
  //   );
  // }

  setupInterceptors() {
    let isRefreshing = false;
    let failedQueue  = [];

    const processQueue = (error, token = null) => {
      failedQueue.forEach((prom) => {
        if (error) {
          prom.reject(error);
        } else {
          prom.resolve(token);
        }
      });
      failedQueue = [];
    };

    // Request Interceptor with Proactive Refresh
    this.api.interceptors.request.use(
      async (config) => {
        config.headers = config.headers || {};

        // Skip proactive refresh for refresh-token endpoint
        if (config.url?.includes('/auth/refresh-token')) {
          const token = localStorage.getItem("QQCRM-accessToken");
          if (token) {
            config.headers["Authorization"] = `Bearer ${token}`;
          }
          config.headers["X-Correlation-ID"] = this.generateCorrelationId();
          return config;
        }

        // Check if token needs proactive refresh
        const token = localStorage.getItem("QQCRM-accessToken");
        if (token && shouldRefreshToken(token, this.refreshThreshold)) {
          const isIdle = activityMonitor.getIsIdle();
          const timeRemaining = getTokenExpiryTime(token);

          if (JWT_CONFIG.DEBUG_MODE) {
            console.log('[ApiService] Token check:', {
              timeRemaining: Math.floor(timeRemaining / 1000) + 's',
              isIdle,
              shouldRefresh: !isIdle && timeRemaining > 0
            });
          }

          // Only refresh if user is active and token hasn't expired yet
          if (!isIdle && timeRemaining > 0) {
            try {
              await this.proactiveRefresh();
            } catch (error) {
              if (JWT_CONFIG.DEBUG_MODE) {
                console.error('[ApiService] Proactive refresh failed:', error);
              }
              // Continue with request, reactive refresh will handle it
            }
          } else if (timeRemaining <= 0) {
            if (JWT_CONFIG.DEBUG_MODE) {
              console.log('[ApiService] Token already expired, will use reactive refresh');
            }
          } else {
            if (JWT_CONFIG.DEBUG_MODE) {
              console.log('[ApiService] User is idle, skipping proactive refresh');
            }
          }
        }

        // Set auth token (might be refreshed)
        const currentToken = localStorage.getItem("QQCRM-accessToken");
        if (currentToken) {
          config.headers["Authorization"] = `Bearer ${currentToken}`;
        }

        config.headers["X-Correlation-ID"] = this.generateCorrelationId();

        if (config.data instanceof FormData) {
          delete config.headers["Content-Type"];
          delete config.headers["content-type"];
        } else if (
          !config.headers["Content-Type"] &&
          !config.headers["content-type"]
        ) {
          config.headers["Content-Type"] = "application/json";
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response Interceptor
    this.api.interceptors.response.use(
      (res) => res,
      async (error) => {
        const originalRequest = error.config;
        const responseData = error.response?.data;

        if (responseData?.code === "TOKEN_EXPIRED") {
          processQueue(new Error("Access token expired"), null);
          localStorage.removeItem("auth-storage");
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          localStorage.removeItem("user");
          localStorage.removeItem("QQCRM-accessToken");
          localStorage.removeItem("QQCRM-refreshToken");
          this.clearAuthToken();
          try {
            const authStore = await import("../store/authStore");
            authStore.default.setState({
              user: null,
              tokens: null,
              isAuthenticated: false,
              isLoading: false,
              error: "Session expired. Please log in again.",
            });
          } catch (storeSyncError) {
            console.error(
              "Failed to reset auth store after token expiration:",
              storeSyncError
            );
          }
          window.location.href = "/auth/login";
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && !originalRequest._retry) {
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers["Authorization"] = `Bearer ${token}`;
                return this.api(originalRequest);
              })
              .catch(Promise.reject);
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            const refreshToken = localStorage.getItem("QQCRM-refreshToken");
            if (
              !refreshToken ||
              refreshToken === "undefined" ||
              refreshToken === "null"
            ) {
              isRefreshing = false;
              processQueue(new Error("Missing refresh token"), null);
              localStorage.removeItem("auth-storage");
              localStorage.removeItem("accessToken");
              localStorage.removeItem("refreshToken");
              localStorage.removeItem("user");
              localStorage.removeItem("QQCRM-accessToken");
              localStorage.removeItem("QQCRM-refreshToken");
              this.clearAuthToken();
              window.location.href = "/auth/login";
              return Promise.reject(error);
            }

            const { data } = await this.api.post("/auth/refresh-token", {
              refreshToken,
            });
            localStorage.setItem("QQCRM-accessToken", data?.data?.accessToken);
            localStorage.setItem(
              "QQCRM-refreshToken",
              data?.data?.refreshToken
            );
            try {
              const authStore = await import("../store/authStore");
              authStore.default.setState((state) => ({
                tokens: {
                  ...(state.tokens || {}),
                  accessToken: data?.data?.accessToken,
                  refreshToken: data?.data?.refreshToken,
                },
                isAuthenticated: true,
              }));
            } catch (storeSyncError) {
              console.error(
                "Failed to sync auth store after token refresh:",
                storeSyncError
              );
            }

            if (JWT_CONFIG.DEBUG_MODE) {
              console.log('[ApiService] Reactive token refresh successful');
              console.log('[ApiService] Note: Using static refresh token (refresh token does not change)');
            }

            originalRequest.headers["Authorization"] = `Bearer ${data.data.accessToken}`;
            this.api.defaults.headers.common[
              "Authorization"
            ] = `Bearer ${data.data.accessToken}`;
            processQueue(null, data.data.accessToken);
            return this.api(originalRequest);
          } catch (err) {
            processQueue(err, null);
            localStorage.clear();
            window.location.href = "/auth/login";
            return Promise.reject(err);
          } finally {
            isRefreshing = false;
          }
        }

        if (!error.response) {
          error.message = "Network error. Please check your connection.";
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Proactively refresh token if expiring soon and user is active
   * Uses promise caching to prevent multiple simultaneous refreshes
   */
  async proactiveRefresh() {
    // If already refreshing, wait for that refresh to complete
    if (this.isProactiveRefreshing && this.proactiveRefreshPromise) {
      if (JWT_CONFIG.DEBUG_MODE) {
        console.log('[ApiService] Waiting for existing proactive refresh...');
      }
      return this.proactiveRefreshPromise;
    }

    this.isProactiveRefreshing = true;
    if (JWT_CONFIG.DEBUG_MODE) {
      console.log('[ApiService] Starting proactive token refresh...');
    }

    this.proactiveRefreshPromise = (async () => {
      try {
        const refreshToken = localStorage.getItem("QQCRM-refreshToken");
        if (!refreshToken || refreshToken === "undefined" || refreshToken === "null") {
          throw new Error('No valid refresh token');
        }

        const { data } = await this.api.post("/auth/refresh-token", {
          refreshToken,
        });

        localStorage.setItem("QQCRM-accessToken", data?.data?.accessToken);
        localStorage.setItem("QQCRM-refreshToken", data?.data?.refreshToken);

        // Update auth store
        try {
          const authStore = await import("../store/authStore");
          authStore.default.setState((state) => ({
            tokens: {
              ...(state.tokens || {}),
              accessToken: data?.data?.accessToken,
              refreshToken: data?.data?.refreshToken,
            },
            isAuthenticated: true,
          }));
        } catch (storeSyncError) {
          console.error('[ApiService] Failed to sync auth store:', storeSyncError);
        }

        this.api.defaults.headers.common["Authorization"] = `Bearer ${data.data.accessToken}`;
        if (JWT_CONFIG.DEBUG_MODE) {
          console.log('[ApiService] Proactive token refresh successful');
          console.log('[ApiService] Note: Using static refresh token (refresh token does not change)');
        }

        return data.data.accessToken;
      } catch (error) {
        if (JWT_CONFIG.DEBUG_MODE) {
          console.error('[ApiService] Proactive refresh failed:', error);
        }
        throw error;
      } finally {
        this.isProactiveRefreshing = false;
        this.proactiveRefreshPromise = null;
      }
    })();

    return this.proactiveRefreshPromise;
  }

  generateCorrelationId() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  }

  setAuthToken(token) {
    if (token) {
      this.api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete this.api.defaults.headers.common["Authorization"];
    }
  }

  clearAuthToken() {
    delete this.api.defaults.headers.common["Authorization"];
  }

  // HTTP Methods
  get(url, config = {}) {
    return this.api.get(url, config);
  }

  post(url, data = {}, config = {}) {
    return this.api.post(url, data, config);
  }

  put(url, data = {}, config = {}) {
    return this.api.put(url, data, config);
  }

  patch(url, data = {}, config = {}) {
    return this.api.patch(url, data, config);
  }

  delete(url, config = {}) {
    return this.api.delete(url, config);
  }

  // File upload
  uploadFile(url, file, onUploadProgress = null) {
    const formData = new FormData();
    formData.append("file", file);

    return this.api.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
    });
  }

  // Download file
  downloadFile(url, filename) {
    return this.api
      .get(url, {
        responseType: "blob",
      })
      .then((response) => {
        const blob = new Blob([response.data]);
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      });
  }
}

const apiService = new ApiService();
export default apiService;
